import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import CopyIcon from '@assets/img/icons/copy.svg?react';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { transform } from 'framer-motion';

import { useUnBindTonWalletMutation } from '@/api/queries/payment';
import { defaultToastConfig } from '@/common/config/toast';
import { copyToClipboard } from '@/common/lib/copyToClipboard';
import { toShortWalletAddress } from '@/common/lib/toShortWalletAddress';
import { H2, H3, StringXSmall } from '@/components/Typography/Typography';
import {
  Button,
  DarkRedButton,
  SecondaryButton,
} from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';

import { ModalContext } from '../Modal/config';

import css from './styles.module.scss';

import linkPng from '/img/icons/icon-link.png';
import linkWebp from '/img/icons/icon-link.webp';
import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';
import walletPng from '/img/wallet.png';
import walletWebp from '/img/wallet.webp';

const TonWalletInfo: FC = () => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [tonConnect] = useTonConnectUI();
  const address = useTonAddress();
  const [unBindTonWallet] = useUnBindTonWalletMutation();

  const copyAddress = (): void => {
    copyToClipboard(address);
    showToast(
      t('Wallet address copied'),
      { img: linkPng, webp: linkWebp },
      defaultToastConfig,
    );
  };

  const disconnectWallet = (): void => {
    tonConnect.disconnect();
    unBindTonWallet();
    localStorage.removeItem('ton_wallet');
    closeModal();
  };

  const calcElipseWidht = transform([0, 428], ['0%', '100%']);

  return (
    <div className="relative w-full  px-4 pt-5 pb-6">
      <Close
        className="absolute top-4 right-4"
        imageVariant="modal"
        variant="modal"
        onClick={closeModal}
      />
      <div
        style={{
          width: calcElipseWidht(283),
        }}
        className={css.elipse}
      ></div>
      <div className="w-full h-full flex flex-col items-center isolate">
        <Picture
          className="w-[7.5rem] h-[7.5rem] mb-2"
          img={walletPng}
          webp={walletWebp}
          alt="wallet"
        />
        <H2 weight="bold" className="mb-6">
          {t('Your wallet')}
        </H2>
        <div className="w-full h-[3.25rem] flex items-center gap-2 rounded-lg p-2 bg-white/10 mb-6">
          <Picture
            className="w-9 h-9"
            img={usdPng}
            webp={usdWebp}
            alt="toncoin"
          />
          <div className="flex flex-col flex-1 gap-1">
            <H3 weight="medium" className={css.textShadow}>
              {toShortWalletAddress(address)}
            </H3>
            <StringXSmall className="!text-white/40">
              The Open Network
            </StringXSmall>
          </div>
          <Button
            variant="transparent"
            className="!w-auto"
            onClick={copyAddress}
          >
            <CopyIcon className="w-8 h-8" />
          </Button>
        </div>
        <SecondaryButton className="mb-3" text={t('Switch wallet')} />
        <DarkRedButton onClick={disconnectWallet} text={t('Disconnect')} />
      </div>
    </div>
  );
};

export { TonWalletInfo };
